import { useEffect, useState } from 'react';
import { OverlayView } from '@react-google-maps/api';

interface StravaStyleMarkerProps {
  position: { lat: number; lng: number };
  accuracy?: number; // GPS accuracy in meters
  color?: string; // Trail color
}

export const StravaStyleMarker = ({ 
  position, 
  accuracy,
  color = '#FF5404' // YBR orange
}: StravaStyleMarkerProps) => {
  const [bounce, setBounce] = useState(false);

  // Trigger bounce animation when position changes
  useEffect(() => {
    setBounce(true);
    const timer = setTimeout(() => setBounce(false), 600);
    return () => clearTimeout(timer);
  }, [position.lat, position.lng]);

  const getAccuracyColor = (acc?: number) => {
    if (!acc) return '#666';
    if (acc < 20) return '#22c55e'; // green
    if (acc < 40) return '#eab308'; // yellow
    return '#ef4444'; // red
  };

  const accuracyColor = getAccuracyColor(accuracy);

  return (
    <OverlayView
      position={position}
      mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
    >
      <div 
        className="strava-marker-container"
        style={{
          position: 'absolute',
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'none',
        }}
      >
        {/* Outer Accuracy Ring - Animated Pulse */}
        <div 
          className="accuracy-ring-outer"
          style={{
            position: 'absolute',
            width: '64px',
            height: '64px',
            borderRadius: '50%',
            border: `2px solid ${accuracyColor}`,
            opacity: 0.3,
            animation: 'pulse-ring 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />

        {/* Inner Accuracy Ring */}
        <div 
          className="accuracy-ring-inner"
          style={{
            position: 'absolute',
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            border: `3px solid ${accuracyColor}`,
            opacity: 0.5,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            transition: 'all 0.5s ease-in-out',
          }}
        />

        {/* Main Dot - Bouncing */}
        <div 
          className={`main-dot ${bounce ? 'bounce' : ''}`}
          style={{
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: `radial-gradient(circle at 30% 30%, ${color}, ${color}dd)`,
            boxShadow: `
              0 0 0 3px rgba(255, 255, 255, 0.9),
              0 0 0 5px ${color}40,
              0 4px 12px rgba(0, 0, 0, 0.4)
            `,
            animation: bounce ? 'bounce-marker 0.6s ease-out' : 'pulse-dot 2s ease-in-out infinite',
            transition: 'all 0.3s ease-in-out',
            position: 'relative',
            zIndex: 10,
          }}
        />

        {/* Accuracy Badge */}
        {accuracy && accuracy < 100 && (
          <div 
            style={{
              position: 'absolute',
              top: '40px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: accuracyColor,
              color: 'white',
              padding: '2px 6px',
              borderRadius: '8px',
              fontSize: '9px',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
              whiteSpace: 'nowrap',
              zIndex: 5,
            }}
          >
            ±{Math.round(accuracy)}m
          </div>
        )}
      </div>
    </OverlayView>
  );
};
